import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Table } from '../../components/Shared/Table';
import { Button } from '../../components/Shared/Button';
import {
  slaDashboardService,
  type OverdueSlaDto,
  type SlaByStepDto,
  type SlaPerformanceDto,
} from '../../services/slaDashboardService';

function getCustomerIdFromStorage(): string {
  return sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';
}

function formatOverdueLabel(minutes: number): string {
  const n = Math.max(0, Math.floor(minutes));
  if (n < 60) {
    return `${n} min`;
  }
  const h = Math.floor(n / 60);
  const min = n % 60;
  if (h < 24) {
    return `${h}h ${min}min`;
  }
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return `${d}d ${hr}h`;
}

function formatAvgMinutes(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) {
    return '—';
  }
  if (m < 60) {
    return `${m.toFixed(0)} min`;
  }
  return `${(m / 60).toFixed(1)} h`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  return moment(iso).format('DD/MM/YYYY HH:mm');
}

const PIE_COLORS = ['#16a34a', '#dc2626'];

export const SlaDashboard: React.FC = () => {
  const customerId = useMemo(() => getCustomerIdFromStorage(), []);
  const [byStep, setByStep] = useState<SlaByStepDto[]>([]);
  const [overdue, setOverdue] = useState<OverdueSlaDto[]>([]);
  const [overduePage, setOverduePage] = useState(1);
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [overduePageSize] = useState(10);
  const [performance, setPerformance] = useState<SlaPerformanceDto[]>([]);
  const [fromDate, setFromDate] = useState(() => moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPerf, setLoadingPerf] = useState(false);

  const loadMain = useCallback(async () => {
    if (!customerId) {
      setLoadError('Cliente não identificado na sessão.');
      setLoading(false);
      return;
    }
    setLoadError(null);
    setLoading(true);
    try {
      const [stepRows, overdueRes] = await Promise.all([
        slaDashboardService.getByStep(customerId),
        slaDashboardService.getOverdue(customerId, overduePage, overduePageSize),
      ]);
      setByStep(stepRows);
      setOverdue(overdueRes.data);
      setOverdueTotal(overdueRes.totalCount);
    } catch (e) {
      setLoadError('Não foi possível carregar o dashboard de SLA.');
      setByStep([]);
      setOverdue([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, overduePage, overduePageSize]);

  const loadPerformance = useCallback(async () => {
    if (!customerId) {
      return;
    }
    setLoadingPerf(true);
    setLoadError(null);
    try {
      const fromIso = moment(fromDate, 'YYYY-MM-DD').startOf('day').toISOString();
      const toIso = moment(toDate, 'YYYY-MM-DD').endOf('day').toISOString();
      const rows = await slaDashboardService.getPerformance(customerId, fromIso, toIso);
      setPerformance(rows);
    } catch {
      setLoadError('Não foi possível carregar a performance por usuário.');
      setPerformance([]);
    } finally {
      setLoadingPerf(false);
    }
  }, [customerId, fromDate, toDate]);

  useEffect(() => {
    void loadMain();
  }, [loadMain]);

  useEffect(() => {
    void loadPerformance();
  }, [loadPerformance]);

  const pieData = useMemo(() => {
    const onT = performance.reduce((s, r) => s + (r.onTimeActions ?? 0), 0);
    const off = performance.reduce((s, r) => s + (r.overdueActions ?? 0), 0);
    if (onT + off === 0) {
      return [];
    }
    return [
      { name: 'No prazo', value: onT },
      { name: 'Fora do prazo', value: off },
    ];
  }, [performance]);

  const byStepRows = byStep;
  const sortedByStep = useMemo(
    () => [...byStepRows].sort((a, b) => a.stepName.localeCompare(b.stepName, 'pt-BR')),
    [byStepRows]
  );

  if (!customerId && !loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard de SLA</h1>
        <p className="text-red-600">Faça login novamente para exibir o cliente (tenant) atual.</p>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard de SLA</h1>
        <p className="text-gray-600 text-sm mt-1">
          SLAs por etapa, itens fora do prazo e desempenho no período.
        </p>
      </div>

      {loadError && <div className="rounded-md bg-red-50 text-red-800 px-4 py-2 text-sm">{loadError}</div>}

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">SLAs por etapa</h2>
        {loading ? (
          <p className="text-gray-500">Carregando…</p>
        ) : (
          <Table
            data={sortedByStep}
            columns={[
              { header: 'Workflow', accessor: 'workflowName' },
              { header: 'Etapa', accessor: 'stepName' },
              { header: 'Em andamento', accessor: 'totalInProgress' },
              { header: 'Concluídos', accessor: 'totalCompleted' },
              { header: 'Vencidos (violados / atraso)', accessor: 'totalOverdue' },
              {
                header: 'Tempo médio de conclusão',
                accessor: 'averageCompletionMinutes',
                cell: (row: SlaByStepDto) => <span>{formatAvgMinutes(row.averageCompletionMinutes)}</span>,
              },
            ]}
          />
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">SLAs vencidos</h2>
        {loading ? (
          <p className="text-gray-500">Carregando…</p>
        ) : (
          <>
            <Table
              data={overdue}
              columns={[
                {
                  header: 'Documento',
                  accessor: 'documentTitle',
                  cell: (row: OverdueSlaDto) => (
                    <Link
                      to={`/documents/${row.documentId}/details`}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      {row.documentTitle}
                    </Link>
                  ),
                },
                { header: 'Etapa', accessor: 'stepName' },
                {
                  header: 'Iniciado em',
                  accessor: 'startedAt',
                  cell: (row: OverdueSlaDto) => formatDateTime(row.startedAt),
                },
                {
                  header: 'Prazo',
                  accessor: 'dueDate',
                  cell: (row: OverdueSlaDto) => (
                    <span>
                      {formatDateTime(row.dueDate)}
                      {row.dueDate != null && (
                        <span className="text-red-600 ml-1">
                          (vencido há {formatOverdueLabel(row.overdueMinutes)})
                        </span>
                      )}
                    </span>
                  ),
                },
                { header: 'Responsáveis', accessor: 'currentAssignees' },
              ]}
            />
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                {overdueTotal === 0
                  ? 'Nenhum SLA vencido no momento.'
                  : `Página ${overduePage} — ${overdueTotal} registro(s) no total.`}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={overduePage <= 1 || loading}
                  onClick={() => setOverduePage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={overduePage * overduePageSize >= overdueTotal || loading}
                  onClick={() => setOverduePage(p => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Performance por usuário</h2>
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <label className="block text-xs text-gray-500 mb-1">De</label>
            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Até</label>
            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => void loadPerformance()}>
            Aplicar período
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 h-64">
            {loadingPerf ? (
              <p className="text-gray-500 text-sm">Carregando gráfico…</p>
            ) : pieData.length === 0 ? (
              <p className="text-gray-500 text-sm">Sem ações concluídas no período para o gráfico.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="lg:col-span-2 overflow-x-auto">
            {loadingPerf ? (
              <p className="text-gray-500">Carregando tabela…</p>
            ) : (
              <Table
                data={performance}
                columns={[
                  { header: 'Usuário', accessor: 'userName' },
                  { header: 'Ações totais', accessor: 'totalActions' },
                  { header: 'No prazo', accessor: 'onTimeActions' },
                  { header: 'Vencidas', accessor: 'overdueActions' },
                  {
                    header: '% no prazo',
                    accessor: 'onTimePercentage',
                    cell: (row: SlaPerformanceDto) => <span>{row.onTimePercentage.toFixed(1)}%</span>,
                  },
                  {
                    header: 'Tempo médio de resposta',
                    accessor: 'averageResponseMinutes',
                    cell: (row: SlaPerformanceDto) => <span>{formatAvgMinutes(row.averageResponseMinutes)}</span>,
                  },
                ]}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SlaDashboard;
